(window.aeOnSectionReady || function (fn) { document.addEventListener('DOMContentLoaded', () => fn(document)); })((root) => {
  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(Number(cents || 0), window.theme?.moneyFormat);
    }
    return `£${(Number(cents || 0) / 100).toFixed(2)}`;
  };

  class AncientProductGallery {
    constructor(container) {
      this.container = container;
      this.mainCarousel = container.querySelector('.ae-product-gallery__main');
      this.thumbnailsWrapper = container.querySelector('.ae-product-gallery__thumbnails');
      this.dotsContainer = container.querySelector('[data-gallery-dots]');
      this.prevBtn = container.querySelector('.ae-product-gallery__nav-btn--prev');
      this.nextBtn = container.querySelector('.ae-product-gallery__nav-btn--next');
      this.mainEmbla = null;
      this.thumbsEmbla = null;
      this.init();
    }

    async init() {
      await this.loadEmbla();
      this.initMainCarousel();
      this.initNavigation();
      this.initThumbnails();
      this.bindEvents();
    }

    async loadEmbla() {
      if (typeof window.EmblaCarousel !== 'undefined') return;
      if (window.__ancientEmblaPromise) return window.__ancientEmblaPromise;

      const candidates = [
        window.aeEmblaAssetUrl,
        'https://cdn.jsdelivr.net/npm/embla-carousel@8.5.2/embla-carousel.umd.js'
      ].filter(Boolean);

      window.__ancientEmblaPromise = candidates.reduce((promise, src) => promise.catch(() => new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[data-ancient-embla-src="${src}"]`);
        if (existing) {
          if (typeof window.EmblaCarousel !== 'undefined') {
            resolve();
            return;
          }
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.dataset.ancientEmblaSrc = src;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
      })), Promise.reject()).catch(() => {
        if (typeof window.EmblaCarousel === 'undefined') {
          throw new Error('Embla failed to load');
        }
      });

      return window.__ancientEmblaPromise;
    }

    initMainCarousel() {
      const viewport = this.mainCarousel?.querySelector('.embla__viewport');
      if (!viewport || typeof window.EmblaCarousel === 'undefined') return;

      this.mainEmbla = window.EmblaCarousel(viewport, {
        loop: false,
        dragFree: false,
        containScroll: 'trimSnaps'
      });

      this.buildDots();
      this.onMainSelect();
      this.mainEmbla.on('select', () => this.onMainSelect());
      this.mainEmbla.on('reInit', () => {
        this.buildDots();
        this.onMainSelect();
        this.updateNavButtons();
      });
    }

    initThumbnails() {
      const viewport = this.thumbnailsWrapper?.querySelector('.embla__viewport');
      if (!viewport || typeof window.EmblaCarousel === 'undefined') return;

      this.thumbsEmbla = window.EmblaCarousel(viewport, {
        loop: false,
        dragFree: true,
        containScroll: 'keepSnaps'
      });

      this.thumbnailsWrapper.querySelectorAll('[data-ae-gallery-thumb]').forEach((btn, index) => {
        btn.addEventListener('click', () => {
          this.mainEmbla?.scrollTo(index);
        });
      });
    }

    initNavigation() {
      if (!this.prevBtn || !this.nextBtn) return;
      this.prevBtn.addEventListener('click', () => this.mainEmbla?.scrollPrev());
      this.nextBtn.addEventListener('click', () => this.mainEmbla?.scrollNext());
      this.updateNavButtons();
    }

    onMainSelect() {
      if (!this.mainEmbla) return;
      const index = this.mainEmbla.selectedScrollSnap();
      this.thumbnailsWrapper?.querySelectorAll('[data-ae-gallery-thumb]').forEach((thumb, i) => {
        thumb.classList.toggle('is-selected', i === index);
      });
      this.thumbsEmbla?.scrollTo(index);
      this.updateDots(index);
      this.updateNavButtons();
    }

    buildDots() {
      if (!this.mainEmbla || !this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.mainEmbla.scrollSnapList().forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ae-product-gallery__dot';
        dot.setAttribute('aria-label', `Go to image ${index + 1}`);
        dot.dataset.index = String(index);
        dot.addEventListener('click', () => this.mainEmbla?.scrollTo(index));
        this.dotsContainer.appendChild(dot);
      });
    }

    updateDots(index) {
      this.dotsContainer?.querySelectorAll('.ae-product-gallery__dot').forEach((dot, dotIndex) => {
        const isSelected = dotIndex === index;
        dot.classList.toggle('is-selected', isSelected);
        if (isSelected) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    updateNavButtons() {
      if (!this.mainEmbla || !this.prevBtn || !this.nextBtn) return;
      this.prevBtn.disabled = !this.mainEmbla.canScrollPrev();
      this.nextBtn.disabled = !this.mainEmbla.canScrollNext();
    }

    bindEvents() {
      this.container.addEventListener('ae:select-media', (event) => {
        const mediaId = String(event.detail?.mediaId || '');
        const slides = Array.from(this.mainCarousel?.querySelectorAll('.embla__slide') || []);
        const targetIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === mediaId);
        if (targetIndex >= 0) this.mainEmbla?.scrollTo(targetIndex);
      });
    }
  }

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    if (!gallery.dataset.initialized) {
      gallery.dataset.initialized = 'true';
      new AncientProductGallery(gallery);
    }
  });

  document.querySelectorAll('[data-ae-purchase-widget]').forEach((widget) => {
    const cards = Array.from(widget.querySelectorAll('[data-ae-purchase-card]'));
    const radios = Array.from(widget.querySelectorAll('[data-ae-purchase-radio]'));
    const sizeOptions = Array.from(widget.querySelectorAll('[data-ae-size-option]'));
    const subscribePrice = widget.querySelector('[data-ae-subscribe-price]');
    const subscribeComparePrice = widget.querySelector('[data-ae-subscribe-compare-price]');
    const onetimePrice = widget.querySelector('[data-ae-onetime-price]');
    const addButton = widget.querySelector('[data-ae-add-to-cart-button]');
    const form = widget.querySelector('[data-ae-product-form]');
    const variantInput = widget.querySelector('[data-ae-variant-input]');
    const sellingPlanInput = widget.querySelector('[data-ae-selling-plan-input]');
    const sellingPlanSelector = widget.querySelector('[data-ae-selling-plan-selector]');
    const gallery = widget.closest('.ae-product-hero__grid')?.querySelector('.ae-product-gallery');
    const subscriptionShell = widget.querySelector('[data-ae-subscription-shell]');
    const frequencyWrap = widget.querySelector('[data-ae-subscription-frequency-wrap]');
    const qtyInput = widget.querySelector('[data-ae-qty-input]');
    const qtyDisplayNumber = widget.querySelector('[data-ae-qty-display-number]');
    const qtyDisplayUnit = widget.querySelector('[data-ae-qty-display-unit]');

    const isSubscribeSelected = () => radios.some((radio) => radio.checked && radio.value === 'subscribe');
    const getQty = () => Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);

    const updateQtyDisplay = () => {
      const qty = getQty();
      if (qtyDisplayNumber) qtyDisplayNumber.textContent = String(qty);
      if (qtyDisplayUnit) {
        const singular = qtyDisplayUnit.getAttribute('data-singular') || 'pouch';
        const plural = qtyDisplayUnit.getAttribute('data-plural') || (singular + 's');
        qtyDisplayUnit.textContent = qty === 1 ? singular : plural;
      }
    };

    let qtyVariantMap = {};
    try {
      const raw = widget.getAttribute('data-qty-variants') || '[]';
      const arr = JSON.parse(raw);
      arr.forEach((v) => { if (v && v.qty) qtyVariantMap[v.qty] = v; });
    } catch (e) { qtyVariantMap = {}; }

    const recalcPrices = () => {
      const qty = getQty();
      let variantCents = parseFloat(widget.dataset.variantPriceCents || '0');
      let compareCents = 0;
      let usingPackVariant = false;

      if (qtyVariantMap[qty]) {
        const v = qtyVariantMap[qty];
        variantCents = parseFloat(v.price);
        compareCents = parseFloat(v.compare) || 0;
        widget.dataset.variantPriceCents = String(variantCents);
        widget.dataset.selectedVariantId = String(v.id);
        if (variantInput) variantInput.value = String(v.id);
        usingPackVariant = true;
      }

      const opt = sellingPlanSelector ? sellingPlanSelector.options[sellingPlanSelector.selectedIndex] : null;

      if (onetimePrice) {
        onetimePrice.textContent = formatMoney(usingPackVariant ? variantCents : variantCents * qty);
      }

      if (opt && opt.getAttribute('data-price-cents')) {
        const planCents = parseFloat(opt.getAttribute('data-price-cents'));
        const planCompare = parseFloat(opt.getAttribute('data-compare-cents') || '0');
        if (usingPackVariant) {
          const pct = parseFloat(opt.getAttribute('data-savings-pct') || '0');
          const subPrice = pct > 0 ? Math.round(variantCents * (1 - pct / 100)) : variantCents;
          if (subscribePrice) subscribePrice.textContent = formatMoney(subPrice);
          if (subscribeComparePrice && subPrice < variantCents) subscribeComparePrice.textContent = formatMoney(variantCents);
        } else {
          if (subscribePrice && planCents) subscribePrice.textContent = formatMoney(planCents * qty);
          if (subscribeComparePrice && planCompare) subscribeComparePrice.textContent = formatMoney(planCompare * qty);
        }
      } else if (subscribePrice && variantCents) {
        subscribePrice.textContent = formatMoney(usingPackVariant ? variantCents : variantCents * qty);
      }

      if (sellingPlanInput) {
        sellingPlanInput.value = (isSubscribeSelected() && opt && opt.value) ? opt.value : '';
      }

      updateQtyDisplay();
    };

    const syncSubscriptionVisibility = (value) => {
      const subscribeSelected = value === 'subscribe';
      subscriptionShell?.classList.toggle('is-hidden', !subscribeSelected);
      frequencyWrap?.toggleAttribute('hidden', !subscribeSelected);
    };

    const syncCards = (value) => {
      cards.forEach((card) => {
        card.classList.toggle('is-active', card.dataset.aePurchaseCard === value);
      });
      radios.forEach((radio) => {
        radio.checked = radio.value === value;
      });
      syncSubscriptionVisibility(value);
      recalcPrices();
    };

    const syncVariant = (option) => {
      const variantId = option.getAttribute('data-variant-id');
      const price = option.getAttribute('data-variant-price');
      const available = option.getAttribute('data-variant-available') === 'true';
      const mediaId = option.getAttribute('data-variant-media-id');

      widget.dataset.selectedVariantId = variantId || '';
      if (price) widget.dataset.variantPriceCents = price;

      if (variantInput) variantInput.value = variantId || '';

      if (addButton) {
        const defaultLabel = addButton.dataset.defaultLabel || addButton.textContent.trim() || 'Add to Cart';
        addButton.dataset.defaultLabel = defaultLabel;
        addButton.disabled = !available;
        addButton.textContent = available ? defaultLabel : 'Sold out';
      }

      if (gallery && mediaId) {
        gallery.dispatchEvent(new CustomEvent('ae:select-media', {
          bubbles: false,
          detail: { mediaId }
        }));
      }

      recalcPrices();
    };

    radios.forEach((radio) => {
      radio.addEventListener('change', () => syncCards(radio.value));
    });

    cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('.ae-purchase-type__select') || event.target.closest('.ae-purchase-type__info-link')) {
          return;
        }
        syncCards(card.dataset.aePurchaseCard);
      });
    });

    sizeOptions.forEach((option) => {
      option.addEventListener('click', () => {
        sizeOptions.forEach((item) => {
          item.classList.remove('is-active', 'ae-flavor-picker__link--selected');
        });
        option.classList.add('is-active', 'ae-flavor-picker__link--selected');
        syncVariant(option);
      });
    });

    sellingPlanSelector?.addEventListener('change', recalcPrices);
    qtyInput?.addEventListener('change', recalcPrices);
    qtyInput?.addEventListener('input', recalcPrices);

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const variantId = variantInput?.value || widget.dataset.selectedVariantId;
      const sellingPlan = sellingPlanInput?.value || '';
      const qtyNum = getQty();
      const usingPackVariant = !!qtyVariantMap[qtyNum];
      const quantity = usingPackVariant ? 1 : qtyNum;
      if (!variantId || typeof window.ancientAddToCart !== 'function') return;
      await window.ancientAddToCart(variantId, {
        button: addButton,
        sellingPlan,
        quantity
      });
    });

    const initialOption = sizeOptions.find((option) => option.classList.contains('is-active')) || sizeOptions[0];
    if (initialOption) syncVariant(initialOption);

    syncSubscriptionVisibility(isSubscribeSelected() ? 'subscribe' : 'onetime');
    recalcPrices();
  });

  document.querySelectorAll('[data-ae-bogo-widget]').forEach((widget) => {
    const cards = Array.from(widget.querySelectorAll('[data-ae-bogo-card]'));
    const radios = Array.from(widget.querySelectorAll('[data-ae-bogo-radio]'));
    const qtyInput = widget.querySelector('[data-ae-bogo-qty-input]');
    const variantInput = widget.querySelector('[data-ae-bogo-variant-input]');
    if (!cards.length || !radios.length || !qtyInput) return;

    const bogoVariantId = widget.dataset.bogoVariantId || '';
    const singleVariantId = widget.dataset.singleVariantId || '';
    const distinctBogoVariant = bogoVariantId && bogoVariantId !== singleVariantId;

    const qtyWrap = widget.querySelector('[data-pw2-qty]');
    const qtyValueEl = qtyWrap ? qtyWrap.querySelector('[data-pw2-qty-value]') : null;
    const qtyMinus = qtyWrap ? qtyWrap.querySelector('[data-pw2-qty-minus]') : null;
    const qtyPlus = qtyWrap ? qtyWrap.querySelector('[data-pw2-qty-plus]') : null;
    const QTY_MAX = 10;
    let qtyMult = 1;
    let currentMode = 'bogo';

    const applyQty = () => {
      let base = 1;
      if (!distinctBogoVariant && currentMode === 'bogo') base = 3;
      qtyInput.value = String(base * qtyMult);
      if (qtyValueEl) qtyValueEl.textContent = String(qtyMult);
      if (qtyMinus) qtyMinus.disabled = qtyMult <= 1;
      if (qtyPlus) qtyPlus.disabled = qtyMult >= QTY_MAX;
      widget.dataset.pw2Mult = String(qtyMult);
      widget.dispatchEvent(new CustomEvent('pw2sub:refresh'));
    };

    const sync = (value) => {
      currentMode = value;
      cards.forEach((card) => {
        card.classList.toggle('is-active', card.dataset.aeBogoCard === value);
      });
      if (distinctBogoVariant && variantInput) {
        variantInput.value = value === 'bogo' ? bogoVariantId : singleVariantId;
      }
      applyQty();
    };

    if (qtyMinus) {
      qtyMinus.addEventListener('click', () => {
        if (qtyMult > 1) { qtyMult -= 1; applyQty(); }
      });
    }
    if (qtyPlus) {
      qtyPlus.addEventListener('click', () => {
        if (qtyMult < QTY_MAX) { qtyMult += 1; applyQty(); }
      });
    }

    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) sync(radio.value);
      });
    });

    cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('button, a, input, label')) return;
        const radio = card.querySelector('[data-ae-bogo-radio]');
        if (radio && !radio.checked) {
          radio.checked = true;
          sync(radio.value);
        }
      });
    });

    const form = widget.querySelector('form');
    const submitBtn = form?.querySelector('[type="submit"]');
    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const variantId = variantInput?.value;
      const quantity = parseInt(qtyInput.value, 10) || 1;
      if (!variantId || typeof window.ancientAddToCart !== 'function') return;
      await window.ancientAddToCart(variantId, {
        button: submitBtn,
        quantity
      });
    });

    const initial = radios.find((r) => r.checked) || radios[0];
    if (initial) sync(initial.value);
  });

  document.querySelectorAll('[data-ae-giftcard-widget]').forEach((widget) => {
    const tiles = Array.from(widget.querySelectorAll('[data-ae-giftcard-tile]'));
    const variantInput = widget.querySelector('[data-ae-giftcard-variant-input]');
    if (!tiles.length || !variantInput) return;

    const gallery = widget.closest('.ae-product-hero__grid')?.querySelector('.ae-product-gallery');
    const form = widget.querySelector('form');
    const submitBtn = form?.querySelector('[type="submit"]');

    const syncTile = (tile) => {
      tiles.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-checked', 'false');
      });
      tile.classList.add('is-active');
      tile.setAttribute('aria-checked', 'true');
      variantInput.value = tile.dataset.variantId;

      const mediaId = tile.getAttribute('data-variant-media-id');
      if (gallery && mediaId) {
        gallery.dispatchEvent(new CustomEvent('ae:select-media', {
          bubbles: false,
          detail: { mediaId }
        }));
      }
    };

    tiles.forEach((tile) => {
      tile.addEventListener('click', () => syncTile(tile));
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const variantId = variantInput.value;
      if (!variantId || typeof window.ancientAddToCart !== 'function') return;
      await window.ancientAddToCart(variantId, {
        button: submitBtn,
        quantity: 1
      });
    });

    const initialTile = tiles.find((t) => t.classList.contains('is-active')) || tiles[0];
    if (initialTile && initialTile.getAttribute('data-variant-media-id') && gallery) {
      gallery.dispatchEvent(new CustomEvent('ae:select-media', {
        bubbles: false,
        detail: { mediaId: initialTile.getAttribute('data-variant-media-id') }
      }));
    }
  });

  document.querySelectorAll('.ae-product-hero__qty').forEach((wrap) => {
    const input = wrap.querySelector('[data-ae-qty-input]');
    const minus = wrap.querySelector('[data-ae-qty-minus]');
    const plus = wrap.querySelector('[data-ae-qty-plus]');
    if (!input) return;

    const clamp = (n) => Math.max(parseInt(input.min || '1', 10), n);

    minus?.addEventListener('click', () => {
      input.value = clamp((parseInt(input.value, 10) || 1) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    plus?.addEventListener('click', () => {
      input.value = clamp((parseInt(input.value, 10) || 1) + 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    input.addEventListener('blur', () => {
      input.value = clamp(parseInt(input.value, 10) || 1);
    });
  });

  document.querySelectorAll('[data-ae-notify-form]').forEach((root) => {
    const form = root.querySelector('[data-ae-notify-form-el]');
    const emailInput = root.querySelector('[data-ae-notify-email]');
    const submitBtn = root.querySelector('[data-ae-notify-submit]');
    const msg = root.querySelector('[data-ae-notify-msg]');
    const publicKey = root.dataset.publicKey;
    const variantId = root.dataset.variantId;
    if (!form || !emailInput || !publicKey || !variantId) return;

    const notifyHero = document.querySelector('.ae-product-hero-section');
    const successText = (notifyHero?.querySelector('[data-notify-success]')?.textContent) ||
      "You're on the list — we'll email you when it's back.";
    const errorText = (notifyHero?.querySelector('[data-notify-error]')?.textContent) ||
      'Something went wrong. Please try again.';

    const setMsg = (text, type) => {
      if (!msg) return;
      msg.textContent = text;
      msg.classList.remove('is-success', 'is-error');
      if (type) msg.classList.add('is-' + type);
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const email = (emailInput.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setMsg('Please enter a valid email address.', 'error');
        return;
      }
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending…';
      }
      try {
        const body = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: '$shopify:::$default:::' + variantId
                }
              }
            }
          }
        };
        const url = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(publicKey);
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
          body: JSON.stringify(body)
        });
        if (!resp.ok && resp.status !== 202) {
          throw new Error('status ' + resp.status);
        }
        form.reset();
        setMsg(successText, 'success');
        if (submitBtn) submitBtn.textContent = '✓ Subscribed';
        document.dispatchEvent(new CustomEvent('ae:notify-subscribed', { detail: { email, variantId } }));
      } catch (err) {
        setMsg(errorText, 'error');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = submitBtn.dataset.label || 'Notify me';
        }
      }
    });
  });
});

(function () {
  if (window.__aeBogoFormDelegated) return;
  window.__aeBogoFormDelegated = true;

  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || form.tagName !== 'FORM') return;
    var widget = form.closest('[data-ae-bogo-widget]');
    if (!widget) return;

    ev.preventDefault();
    ev.stopPropagation();

    var variantInput = widget.querySelector('[data-ae-bogo-variant-input]');
    var qtyInput = widget.querySelector('[data-ae-bogo-qty-input]');
    var submitBtn = form.querySelector('[type="submit"]');
    var variantId = variantInput ? variantInput.value : '';
    var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    if (!variantId || typeof window.ancientAddToCart !== 'function') return;
    var planInput = widget.querySelector('[data-ae-bogo-plan-input]');
    var atcOptions = { button: submitBtn, quantity: quantity };
    if (planInput && planInput.value) atcOptions.sellingPlan = planInput.value;
    window.ancientAddToCart(variantId, atcOptions);
  }, true);
})();


/* ── Purchase Widget v2 (Trial): subscribe box on the BOGO clone ── */
(function () {
  document.querySelectorAll('[data-pw2sub]').forEach(function (box) {
    if (box.dataset.pw2subInit === '1') return;
    box.dataset.pw2subInit = '1';

    var widget = box.closest('[data-ae-bogo-widget]');
    var cfgEl = box.querySelector('[data-pw2sub-config]');
    var cfg = { plans: [] };
    if (cfgEl) { try { cfg = JSON.parse(cfgEl.textContent); } catch (e) {} }
    var plans = cfg.plans || [];
    if (!plans.length) {
      box.hidden = true;
      if (widget) widget.classList.add('ae-pw2--nosub');
      return;
    }
    box.hidden = false;

    var planInput = widget ? widget.querySelector('[data-ae-bogo-plan-input]') : null;
    var toggle = box.querySelector('[data-pw2sub-toggle]');
    var pct = box.querySelector('[data-pw2sub-pct]');
    var expand = box.querySelector('[data-pw2sub-expand]');
    var panel = box.querySelector('[data-pw2sub-panel]');
    var freq = box.querySelector('[data-pw2sub-frequency]');

    function priceEls(cardSel) {
      if (!widget) return null;
      var card = widget.querySelector(cardSel);
      if (!card) return null;
      var wrap = card.querySelector('.ae-bogo__prices');
      if (!wrap) return null;
      var current = wrap.querySelector('.ae-bogo__price-current');
      var compare = wrap.querySelector('.ae-bogo__price-compare');
      return {
        wrap: wrap,
        current: current,
        compare: compare,
        origCurrent: current ? current.textContent : '',
        origCompare: compare ? compare.textContent : null
      };
    }
    var singleEls = priceEls('.ae-bogo__card--single');
    var bogoEls = priceEls('.ae-bogo__card--bogo');

    var unitCents = widget ? parseInt(widget.dataset.unitCents, 10) || 0 : 0;
    var bogoCents = widget ? parseInt(widget.dataset.bogoCents, 10) || 0 : 0;
    var singleCompareCents = widget ? parseInt(widget.dataset.singleCompareCents, 10) || 0 : 0;
    var bogoCompareCents = widget ? parseInt(widget.dataset.bogoCompareCents, 10) || 0 : 0;
    var moneySym = '£';
    if (singleEls && singleEls.origCurrent) {
      var symMatch = singleEls.origCurrent.trim().match(/^[^0-9]+/);
      if (symMatch && symMatch[0].trim()) moneySym = symMatch[0].trim();
    }
    function fmt(cents) {
      var v = cents / 100;
      return moneySym + (v % 1 === 0 ? String(v) : v.toFixed(2));
    }
    function mult() {
      return widget ? (parseInt(widget.dataset.pw2Mult, 10) || 1) : 1;
    }

    function setPrices(els, currentText, compareText) {
      if (!els || !els.current) return;
      els.current.textContent = currentText;
      var showCompare = compareText && compareText !== currentText;
      if (showCompare) {
        if (!els.compare) {
          els.compare = document.createElement('span');
          els.compare.className = 'ae-bogo__price-compare';
          els.wrap.appendChild(els.compare);
        }
        els.compare.textContent = compareText;
        els.compare.hidden = false;
      } else if (els.compare) {
        els.compare.hidden = true;
      }
    }
    function restorePrices(els) {
      if (!els || !els.current) return;
      els.current.textContent = els.origCurrent;
      if (els.compare) {
        if (els.origCompare === null) {
          els.compare.hidden = true;
        } else {
          els.compare.textContent = els.origCompare;
          els.compare.hidden = false;
        }
      }
    }

    if (freq) {
      freq.innerHTML = '';
      plans.forEach(function (pl) {
        var opt = document.createElement('option');
        opt.value = pl.id;
        opt.textContent = pl.label;
        freq.appendChild(opt);
      });
    }

    function currentPlan() {
      if (freq && freq.value) {
        for (var i = 0; i < plans.length; i++) {
          if (String(plans[i].id) === String(freq.value)) return plans[i];
        }
      }
      return plans[0];
    }

    function apply() {
      var on = !!(toggle && toggle.checked);
      var plan = currentPlan();
      if (planInput) planInput.value = on && plan ? String(plan.id) : '';
      box.classList.toggle('is-on', on);
      if (widget) widget.classList.toggle('ae-pw2--nosub', !on);
      if (pct && plan) pct.textContent = plan.pct > 0 ? plan.pct + '%' : '';
      var m = mult();
      if (on && plan && (plan.spc || plan.sp)) {
        if (plan.spc) {
          var spc = plan.spc * m;
          var scc = unitCents * m;
          setPrices(singleEls, fmt(spc), scc > spc ? fmt(scc) : null);
        } else {
          setPrices(singleEls, plan.sp, plan.sc);
        }
        if (plan.bpc) {
          var bpc = plan.bpc * m;
          var bcc = bogoCents * m;
          setPrices(bogoEls, fmt(bpc), bcc > bpc ? fmt(bcc) : null);
        } else if (plan.bp) {
          setPrices(bogoEls, plan.bp, plan.bc);
        }
      } else if (m > 1 && unitCents) {
        setPrices(singleEls, fmt(unitCents * m), singleCompareCents > unitCents ? fmt(singleCompareCents * m) : null);
        setPrices(bogoEls, fmt(bogoCents * m), bogoCompareCents > bogoCents ? fmt(bogoCompareCents * m) : null);
      } else {
        restorePrices(singleEls);
        restorePrices(bogoEls);
      }
    }

    if (toggle) {
      toggle.addEventListener('change', function () {
        if (toggle.checked && panel && panel.hidden) {
          panel.hidden = false;
          if (expand) expand.setAttribute('aria-expanded', 'true');
        }
        apply();
      });
    }
    if (expand && panel) {
      expand.addEventListener('click', function () {
        var open = panel.hidden;
        panel.hidden = !open;
        expand.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    if (freq) freq.addEventListener('change', apply);
    if (widget) widget.addEventListener('pw2sub:refresh', apply);
    apply();
  });
})();
